import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  MAT_MENU_DEFAULT_OPTIONS,
  MatMenuModule,
  MatMenuTrigger,
} from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { NavigationEnd, Router } from '@angular/router';
import { EmailTagClient } from '@app-services/api/clients/email-tag.client';
import {
  BaseGetByFileIdRequest,
  BaseGetByIdRequest,
} from '@app-types/base/base';
import {
  EmailListItemContract,
  IGroup,
  TransformEmailListItemContract,
} from '@app-types/api/email';
import { MailFolderType } from '@app-types/enums/mail-folder.type';
import { MobileObserverService } from '@app-services/adaptive/mobile-observer.service';
import { EmailAddGroupsDialogComponent } from '../email/email-add-groups-dialog/email-add-groups-dialog.component';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { EmailSearchOrder } from '@app-types/enums/email.search-order';
import { TranslateModule } from '@ngx-translate/core';
import { PermissionService } from 'src/app/services/permission/permission.service';
import { MatSelectModule } from '@angular/material/select';
import { SearchInfo, SearchParams } from '@app-types/search';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoaderComponent } from '../common/loader/loader.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatPaginatorModule } from '@angular/material/paginator';
import { SomethingWentWrongComponent } from '../common/error/something-went-wrong/something-went-wrong.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_CHIPS_DEFAULT_OPTIONS,
  MatChipsModule,
} from '@angular/material/chips';
import {
  EMAIL_QUERY_PARAM,
  GROUPS_EXAMPLE,
  STORAGE_NAMES,
  TAGS_EXAMPLE,
} from '@app-shared/constants/constants';
import { filter } from 'rxjs/operators';
import {
  decodeInfoMailAccount,
  encodeInfoMailAccount,
} from '@app-utils/search-params';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { SearchTypeEnum } from '@app-types/enums/filters';
import {
  getAllFoldersMailAccount,
  getFoldersAndSubfoldersId,
} from '@app-utils/folders';
import { PermissionType } from '@app-types/enums/permission-type';
import { NgTemplateOutlet } from '@angular/common';
import { DrawerService } from '@app-services/drawer.service';
import { EmailNoteListComponent } from '../email/email-note-list/email-note-list.component';
import { EmailAuditComponent } from '../common/email/email-audit/email-audit.component';
import { EmailCreateOrUpdateComponent } from '../email/email-create-or-update/email-create-or-update.component';
import { ImportEmlComponent } from '../import-eml/import-eml.component';
import { EmailNotificationsDialogComponent } from '../common/email/email-notifications-dialog/email-notifications-dialog.component';
import { EmailDetailDialogComponent } from '../email/email-detail-dialog/email-detail-dialog.component';
import {
  ALL,
  NOT_VALIDATED,
  VALIDATED,
} from '@app-shared/constants/validatedEmail';
import { Subscription } from 'rxjs';
import { EmailMetaComponent } from '@app-components/email/email-meta/email-meta.component';
import { MatCardModule } from '@angular/material/card';
import { SkeletonTableComponent } from '@app-components/common/skeletons/skeleton-table/skeleton-table.component';
import { ROUTES } from '@app-shared/constants/routes/fe.routes';
import { TableHeaderComponent } from '@app-components/common/table-header/table-header.component';
import { MatSortModule, Sort } from '@angular/material/sort';
import { OrderDirection } from '@app-types/enums/order-direction';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TourMatMenu } from 'ngx-ui-tour-md-menu';
import { LocalStorageService } from '@app-services/local-storage.service';
import { MailFolderUnreadCount } from '@app-types/api/mail-folder';
import { EmailAddTagsDialogComponent } from '@app-components/email/email-add-tags-dialog/email-add-tags-dialog.component';
import { TagContract } from '@app-types/api/tag';
import { EmailActionButtonsComponent } from '@app-components/email/email-action-buttons/email-action-buttons.component';
import { EmailsHeaderActionButtonsComponent } from '@app-components/emails/emails-header-action-buttons/emails-header-action-buttons.component';
import { EmailsHeaderActionButtonsMobileComponent } from '@app-components/emails/emails-header-action-buttons-mobile/emails-header-action-buttons-mobile.component';
import { EmailsMobileComponent } from '@app-components/emails/emails-mobile/emails-mobile.component';
import { EmailsDesktopComponent } from '@app-components/emails/emails-desktop/emails-desktop.component';
import { EmailSelectAllCheckboxComponent } from '@app-components/email/email-select-all-checkbox/email-select-all-checkbox.component';
import { EmailsService } from '@app-services/emails/emails.service';
import { EmailsScrollService } from '@app-services/emails/emails-scroll.service';
import { EmailExportClient } from '@app-services/api/clients/email-export-client';
import { TempFileClient } from '@app-services/api/clients/temp-file.client';
import { ButtonActionsService } from '@app-services/emails/button-actions/button-actions.service';
import { ActionsModalsService } from '@app-services/emails/button-actions/actions-modal.service';
import { ActionsService } from '@app-services/emails/button-actions/actions.service';
import { EmailsPageStateService } from '@app-services/emails/emails-page-state.service';
import { TagType } from '@app-types/enums/tags';
import { ConfirmCloseDialogComponent } from '@app-components/common/confirm-close-dialog/confirm-close-dialog.component';

const { EMAILS } = ROUTES;
const { PRE_RELEASE } = STORAGE_NAMES;

@Component({
  selector: 'app-mails-content',
  templateUrl: './emails-content.component.html',
  styleUrls: ['./emails-content.component.scss'],
  imports: [
    TranslateModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    LoaderComponent,
    MatFormFieldModule,
    MatSelectModule,
    MatPaginatorModule,
    SomethingWentWrongComponent,
    MatCheckboxModule,
    MatChipsModule,
    MatTableModule,
    MatMenuModule,
    EmailNoteListComponent,
    EmailAuditComponent,
    EmailCreateOrUpdateComponent,
    ImportEmlComponent,
    EmailNotificationsDialogComponent,
    EmailDetailDialogComponent,
    EmailMetaComponent,
    MatCardModule,
    SkeletonTableComponent,
    TableHeaderComponent,
    MatSortModule,
    MatSlideToggle,
    ...TourMatMenu,
    NgTemplateOutlet,
    EmailAddTagsDialogComponent,
    EmailAddGroupsDialogComponent,
    EmailActionButtonsComponent,
    EmailsHeaderActionButtonsComponent,
    EmailsHeaderActionButtonsMobileComponent,
    EmailsMobileComponent,
    EmailsDesktopComponent,
    EmailSelectAllCheckboxComponent,
  ],
  providers: [
    {
      provide: MAT_CHIPS_DEFAULT_OPTIONS,
      useValue: { hideSingleSelectionIndicator: true },
    },
    {
      provide: MAT_MENU_DEFAULT_OPTIONS,
      useValue: { overlayPanelClass: 'panelClassMenu' },
    },
  ],
})
export class EmailsContentComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  public actionsModals: ActionsModalsService;
  public actions: ActionsService;

  public isExporting = false;

  public useMobileView = false;
  private useMobileViewSubscription: Subscription;

  public menuTopLeftPosition = { x: '0', y: '0' };
  public openedEmailInfo: any;
  public mailFolderType = MailFolderType;

  public mouseCoordinatesX: number;
  public mouseCoordinatesY: number;
  public isLoadingEML = false;

  routeSubscribe$: Subscription;

  preRelease = false;
  setInterval: NodeJS.Timeout;
  setIntervalTag: NodeJS.Timeout;
  setIntervalGroup: NodeJS.Timeout;

  @ViewChild(MatMenuTrigger, { static: true }) matMenuTrigger: MatMenuTrigger;
  @ViewChild('metaDrawer') metaDrawer: TemplateRef<any>;
  @ViewChild('notesDrawer') notesDrawer: TemplateRef<any>;
  @ViewChild('auditDrawer') auditDrawer: TemplateRef<any>;
  @ViewChild('createOrUpdateDrawer') createOrUpdateDrawer: TemplateRef<any>;
  @ViewChild('importEMLIsDrawer') importEMLIsDrawer: TemplateRef<any>;
  @ViewChild('notificationDrawer') notificationDrawer: TemplateRef<any>;
  @ViewChild('emailDrawer') emailDrawer: TemplateRef<any>;
  @ViewChild('addTagDrawer') addTagDrawer: TemplateRef<any>;
  @ViewChild('addGroupDrawer') addGroupDrawer: TemplateRef<any>;
  @ViewChild(EmailsDesktopComponent)
  emailsDesktopComponent: EmailsDesktopComponent;

  constructor(
    private router: Router,
    public dialog: MatDialog,
    private mobileObserverService: MobileObserverService,
    private matchError: MatchError,
    private emailTagClient: EmailTagClient,
    private emailExportClient: EmailExportClient,
    private tempFileClient: TempFileClient,
    private permissionService: PermissionService,
    public userWorkspaceService: UserWorkspaceService,
    private drawerService: DrawerService,
    private localStorageService: LocalStorageService,
    protected emailsService: EmailsService,
    protected emailsScrollService: EmailsScrollService,
    protected buttonActionsService: ButtonActionsService,
    public emailsPageStateService: EmailsPageStateService
  ) {}

  async ngOnInit(): Promise<void> {
    this.useMobileViewSubscription = this.mobileObserverService
      .mobileObserver()
      .subscribe(isMobile => (this.useMobileView = isMobile));

    if (!this.userWorkspaceService.mailAccounts.length) {
      return;
    }

    await this.setInfoOpenMAilAccount(window.location.href, true, true);
    this.openNewEmailFromContacts();

    this.routeSubscribe$ = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(async () => {
        this.setInterval && clearInterval(this.setInterval);
        this.setIntervalTag && clearInterval(this.setIntervalTag);
        this.setIntervalGroup && clearInterval(this.setIntervalGroup);
        this.emailsScrollService.resetScrollConfig();
        this.emailsPageStateService.resetData();

        const loaderTimeout = setTimeout(() => {
          this.emailsPageStateService.showLoader = true;
        }, 500);
        await this.setInfoOpenMAilAccount(window.location.href, true);
        loaderTimeout && clearTimeout(loaderTimeout);
      });
  }

  public async createEmail(): Promise<any> {
    this.actionsModals.openCreateOrUpdateModalForm(null);
  }

  addEmail(): void {
    const newEmail =
      this.emailsPageStateService.dataSource.data[
        Math.floor(
          Math.random() * this.emailsPageStateService.dataSource.data.length
        )
      ];
    this.emailsPageStateService.dataSource.data = [
      { ...newEmail, newEmail: true, seen: false },
      ...this.emailsPageStateService.dataSource.data,
    ];
    this.userWorkspaceService.mailAccId =
      this.emailsPageStateService.infoOpenedMailAccount.acc;
    this.userWorkspaceService.folderId =
      this.emailsPageStateService.infoOpenedMailAccount.folder;
    this.emailsPageStateService.counts = {
      totalCount: (this.emailsPageStateService.counts?.totalCount ?? 0) + 1,
    };
    this.userWorkspaceService.mailAccountBadges =
      this.userWorkspaceService.mailAccountBadges.map(el => {
        return el.mailAccountId ===
          this.emailsPageStateService.infoOpenedMailAccount.acc
          ? { ...el, badge: el.badge + 1 }
          : el;
      });

    const currentFolderCount = this.userWorkspaceService.folderCounts[
      this.emailsPageStateService.infoOpenedMailAccount.acc ?? ''
    ]?.find(
      el =>
        el.mailFolderId ===
        this.emailsPageStateService.infoOpenedMailAccount.folder
    );

    const newBages: MailFolderUnreadCount[] = currentFolderCount
      ? this.userWorkspaceService.folderCounts[
          this.emailsPageStateService.infoOpenedMailAccount.acc ?? ''
        ]?.map(e => {
          return e.mailFolderId ===
            this.emailsPageStateService.infoOpenedMailAccount.folder
            ? { ...e, count: e.count + 1 }
            : e;
        })
      : [
          ...(this.userWorkspaceService.folderCounts[
            this.emailsPageStateService.infoOpenedMailAccount.acc ?? ''
          ] ?? []),
          {
            mailFolderId:
              this.emailsPageStateService.infoOpenedMailAccount.folder ?? 0,
            count: 1,
          },
        ];

    this.userWorkspaceService.folderCounts = {
      ...this.userWorkspaceService.folderCounts,
      [this.emailsPageStateService.infoOpenedMailAccount.acc ?? '']: newBages,
    };

    setTimeout(() => {
      this.userWorkspaceService.mailAccId = undefined;
      this.userWorkspaceService.folderId = undefined;
    }, 1000);
  }

  startUpdateMails(): void {
    this.emailsPageStateService.dataSource.data.length && this.addEmail();
    this.emailsPageStateService.dataSource.data.length &&
      (this.setInterval = setInterval(() => this.addEmail(), 7500));

    this.emailsPageStateService.dataSource.data.length &&
      (this.setIntervalTag = setInterval(() => {
        this.emailsPageStateService.dataSource.data =
          this.emailsPageStateService.dataSource.data.map((el, index) => {
            return index === 2
              ? {
                  ...el,
                  newEmail: false,
                  tags: [
                    ...(el?.tags ?? []),
                    {
                      mailAccountId:
                        this.emailsPageStateService.infoOpenedMailAccount.acc ??
                        0,
                      tag: TAGS_EXAMPLE[
                        Math.floor(Math.random() * TAGS_EXAMPLE.length)
                      ],
                      tagId: new Date().getTime(),
                      type: TagType.Email,
                    },
                  ],
                }
              : el;
          });
      }, 9000));

    this.emailsPageStateService.dataSource.data.length &&
      (this.setIntervalGroup = setInterval(() => {
        this.emailsPageStateService.dataSource.data =
          this.emailsPageStateService.dataSource.data.map((el, index) => {
            return index === 1
              ? {
                  ...el,
                  newEmail: false,
                  groups: [
                    ...(el?.groups ?? []),
                    {
                      group:
                        GROUPS_EXAMPLE[
                          Math.floor(Math.random() * GROUPS_EXAMPLE.length)
                        ],
                      groupId: new Date().getTime(),
                    },
                  ],
                }
              : el;
          });
      }, 11000));
  }

  async setInfoOpenMAilAccount(
    url: string,
    getFolders = false,
    getCount?: boolean
  ): Promise<void> {
    const newUrl = new URL(url);
    const search: string = newUrl.searchParams.get(EMAIL_QUERY_PARAM) ?? '';
    const info: SearchInfo = decodeInfoMailAccount(search);
    const showLoader =
      info?.type !== this.emailsPageStateService.infoOpenedMailAccount?.type;
    this.emailsPageStateService.infoOpenedMailAccount = info;
    this.emailsPageStateService.updateConfigPage({
      isAllFoldersSelected: info?.searchType === SearchTypeEnum.allFolders,
    });

    this.emailsScrollService.scrollToTop();

    this.emailsPageStateService.searchParams =
      info?.filter ?? new SearchParams();
    this.emailsPageStateService.searchParams.hideNotifications =
      info?.hideNotifications !== false;
    this.emailsPageStateService.searchParams.validated =
      info?.searchType === SearchTypeEnum.allFolders
        ? ALL
        : info?.tab === 0 || (!info?.tab && info.type === MailFolderType.Inbox)
          ? VALIDATED
          : info?.tab === 1
            ? NOT_VALIDATED
            : ALL;

    this.emailsPageStateService.searchParams.reassigned = this
      .emailsPageStateService.searchParams?.reassignedId
      ? true
      : this.emailsPageStateService.isAllFoldersSelected() ||
          info?.type === this.mailFolderType.View
        ? null
        : info?.tab === 2;

    this.emailsPageStateService.searchParams.fullText = info?.search ?? null;

    if (info?.type === this.mailFolderType.Spam) {
      this.emailsPageStateService.searchParams.isSpam = true;
    }
    if (info?.type === this.mailFolderType.Archive) {
      this.emailsPageStateService.searchParams.isArchive = true;
    }
    if (info?.type === this.mailFolderType.Trash) {
      this.emailsPageStateService.searchParams.deleted = true;
    }
    this.emailsPageStateService.searchOrder = info?.order
      ? info.order
      : this.emailsPageStateService.searchParams.searchOrder;
    this.emailsPageStateService.orderDirection = info?.dir
      ? info.dir
      : this.emailsPageStateService.searchParams.orderDirection;

    this.emailsPageStateService.searchParams.seen =
      info.filter?.seen ?? this.emailsPageStateService.searchParams.seen;
    this.emailsPageStateService.onlyUnseen = info?.seen ?? null;
    const mailAccountFolders = this.userWorkspaceService.userFolders.filter(
      el =>
        el.mailAccountId ===
        (info?.acc ?? this.userWorkspaceService.mailAccounts[0].mailAccountId)
    );

    if (
      info?.folder &&
      (!info?.searchType || info?.searchType === SearchTypeEnum.currSelections)
    ) {
      this.emailsPageStateService.updateConfigPage({
        selectedMailFolderIds: [info.folder],
        selectedMailFolderId: info.folder,
      });
    }

    if (
      info?.folder &&
      info?.searchType === SearchTypeEnum.currSelectionsPlusSubfolders
    ) {
      this.emailsPageStateService.updateConfigPage({
        selectedMailFolderIds: getFoldersAndSubfoldersId(
          info.folder,
          mailAccountFolders
        ),
        selectedMailFolderId: info.folder,
      });
    }

    if (info?.searchType === SearchTypeEnum.allFolders) {
      info?.acc &&
        this.emailsPageStateService.updateConfigPage({
          selectedMailFolderIds: getAllFoldersMailAccount(
            info.acc,
            mailAccountFolders
          ),
          selectedMailFolderId: null,
        });
    }

    const canFinalDelete = await this.permissionService.hasPermissionOver(
      null,
      this.emailsPageStateService.infoOpenedMailAccount?.acc ?? null,
      PermissionType.CanFinalDelete
    );

    if (this.emailsPageStateService.infoOpenedMailAccount?.type) {
      this.emailsService.setInitState(
        this.emailsPageStateService.infoOpenedMailAccount.type,
        canFinalDelete,
        this.emailsPageStateService.searchParams.reassigned
      );
      this.emailsService.setColumns(
        this.emailsPageStateService.infoOpenedMailAccount.type
      );
    }

    await this.emailsService.loadData(
      getFolders,
      getCount !== undefined
        ? getCount
        : this.emailsScrollService.offset() === 0,
      showLoader
    );

    this.emailsPageStateService.updateConfigPage({
      defaultOrderDirection:
        this.emailsPageStateService.orderDirection === OrderDirection.Ascending
          ? 'asc'
          : 'desc',
      defaultSortOrder:
        this.emailsPageStateService.searchOrders?.find(
          el => el.id === this.emailsPageStateService.searchOrder
        )?.name ?? '',
    });

    this.preRelease = this.localStorageService.getData(PRE_RELEASE);
    this.preRelease && this.startUpdateMails();
  }

  onRightClick(event: MouseEvent, item: any): void {
    // preventDefault avoids to show the visualization of the right-click menu of the browser
    event.preventDefault();

    // we record the mouse position in our object
    this.menuTopLeftPosition.x = event.clientX + 'px';
    this.menuTopLeftPosition.y = event.clientY + 'px';

    if (this.matMenuTrigger) {
      // we open the menu
      // we pass to the menu the information about our object
      this.matMenuTrigger.menuData = { item };

      // we open the menu
      this.matMenuTrigger.openMenu();
    }
  }

  public getInfoAboutOpenedEmail(openedEmail: any): void {
    this.openedEmailInfo = openedEmail;
  }

  public setSeenOpened(obj: any): void {
    this.userWorkspaceService.updateUnreadList([obj.emailId], true);
    this.emailsPageStateService.changeDataTable([obj.emailId], 'seen', obj.b);
  }

  importEML(): void {
    this.drawerService.openDrawer(
      this.importEMLIsDrawer,
      () => {
        this.closeImportEml();
      },
      'create-email'
    );
  }

  async exportExcel(): Promise<void> {
    this.isExporting = true;
    try {
      const request = {
        offset: 1,
        fetch: 1,
        filter: this.emailsPageStateService.searchParams,
        mailFolderIds: this.emailsPageStateService.selectedMailFolderIds(),
      };

      const fileResponse = await this.emailExportClient.exportExcel(request);
      const downloadRequest = new BaseGetByFileIdRequest(
        fileResponse.result.fileId
      );
      await this.tempFileClient.download(downloadRequest);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isExporting = false;
    }
  }

  onStartRecording(e: MouseEvent): void {
    if (e.button === 0) {
      this.mouseCoordinatesX = e.x;
      this.mouseCoordinatesY = e.y;
    }
  }

  onFinishRecording(e: MouseEvent, row: EmailListItemContract): void {
    if (e.button === 0) {
      this.mouseCoordinatesX === e.x &&
        this.mouseCoordinatesY === e.y &&
        this.actionsModals.emailDetails(row);
    }
  }

  get countTotal(): number {
    return this.emailsPageStateService.counts?.totalCount || 0;
  }

  public closeImportEml(): void {
    if (this.isLoadingEML) {
      const dialogRef = this.dialog.open(ConfirmCloseDialogComponent, {
        autoFocus: false,
        data: {
          title: 'confirmCloseUpload',
          text: 'uploadDeletion',
        },
      });
      dialogRef.afterClosed().subscribe(async ({ isClose }) => {
        if (isClose) {
          this.drawerService.closeDrawer();
          this.isLoadingEML = false;
        }
      });
    } else {
      this.drawerService.closeDrawer();
    }
  }

  public refreshNotes(): void {
    const emailId =
      this.emailsPageStateService.notesMailId ??
      this.emailsPageStateService.openedEmail?.emailId ??
      this.emailsPageStateService.updatedEmail?.emailId;

    if (!emailId) return;

    this.emailsPageStateService.changeDataTable([emailId], 'notesCount', 1);
  }

  changePage(isUpdateSort = true): void {
    if (
      this.emailsPageStateService.infoOpenedMailAccount?.acc &&
      this.emailsPageStateService.infoOpenedMailAccount?.folder &&
      this.emailsPageStateService.infoOpenedMailAccount?.type
    ) {
      const data: SearchInfo = {};
      if (isUpdateSort) {
        this.emailsPageStateService.searchOrder &&
          (data.order = this.emailsPageStateService.searchOrder);
        this.emailsPageStateService.orderDirection &&
          (data.dir = this.emailsPageStateService.orderDirection);
      }
      this.emailsPageStateService.onlyUnseen === false &&
        (data.seen = this.emailsPageStateService.onlyUnseen);
      this.emailsPageStateService.infoOpenedMailAccount?.hideNotifications ===
        false &&
        (data.hideNotifications =
          this.emailsPageStateService.infoOpenedMailAccount.hideNotifications);
      this.emailsPageStateService.infoOpenedMailAccount?.search &&
        (data.search =
          this.emailsPageStateService.infoOpenedMailAccount.search);
      this.emailsPageStateService.infoOpenedMailAccount?.searchType &&
        (data.searchType =
          this.emailsPageStateService.infoOpenedMailAccount.searchType);
      this.emailsPageStateService.infoOpenedMailAccount?.filter &&
        (data.filter = {
          ...this.emailsPageStateService.infoOpenedMailAccount.filter,
          seen: this.emailsPageStateService.onlyUnseen,
        });
      this.emailsPageStateService.infoOpenedMailAccount?.tab &&
        (data.tab = this.emailsPageStateService.infoOpenedMailAccount.tab);

      const infoMailAcc = encodeInfoMailAccount(
        this.emailsPageStateService.infoOpenedMailAccount.acc,
        this.emailsPageStateService.infoOpenedMailAccount.folder,
        this.emailsPageStateService.infoOpenedMailAccount.type,
        data
      );

      this.router.navigate([EMAILS], {
        queryParams: infoMailAcc
          ? {
              [EMAIL_QUERY_PARAM]: infoMailAcc,
            }
          : {},
      });
    }
  }

  onCloseGroupModalForm(): void {
    this.emailsPageStateService.emailIdsForGroup = null;
  }

  onAddGroup({ groups }: { groups: IGroup[] }): void {
    this.emailsPageStateService.emailIdsForGroup &&
      this.emailsPageStateService.changeDataTable(
        this.emailsPageStateService.emailIdsForGroup,
        'groups',
        groups
      );
  }

  onCloseEditModalForm(): void {
    this.emailsPageStateService.emailsForTags = null;
  }

  onCreateTag($event: { tags: TagContract[]; ids: number[] }): void {
    this.emailsPageStateService.changeDataTable(
      $event.ids,
      'tags',
      $event.tags
    );
  }

  public async searchOrderChange($event: any): Promise<void> {
    this.emailsPageStateService.searchOrder = $event as EmailSearchOrder;
    this.emailsScrollService.scrollToTop();
    await this.changePage();
  }

  public async closeCreateOrUpdateModalForm(): Promise<any> {
    if (
      this.emailsPageStateService.infoOpenedMailAccount.type ===
      this.mailFolderType.Draft
    ) {
      this.emailsService.reload();
    }
    if (
      this.emailsPageStateService.updatedEmail?.emailId &&
      this.emailsPageStateService.infoOpenedMailAccount.type !==
        this.mailFolderType.Draft
    ) {
      this.getTags(true, this.emailsPageStateService.updatedEmail.emailId);
    }
    this.emailsPageStateService.updatedEmail = null;
    this.emailsPageStateService.infoInitialContactsTo = null;
  }

  private openNewEmailFromContacts(): void {
    this.drawerService.closeDrawer();
    const currentAccountId =
      this.emailsPageStateService.infoOpenedMailAccount?.acc;
    const mailAccIdSend =
      this.emailsPageStateService.infoInitialContactsTo?.mailAccountId;
    const contacts = this.emailsPageStateService.infoInitialContactsTo?.to;
    if (!contacts?.length || currentAccountId !== mailAccIdSend) {
      return;
    }

    void this.createEmail();
  }

  public async getTags(isSeen: boolean, emailId: number): Promise<void> {
    try {
      const tagsResponse = await this.emailTagClient.getEmailTags(
        new BaseGetByIdRequest(emailId)
      );
      this.emailsPageStateService.changeObjectDataTable([emailId], {
        seen: isSeen,
        tags: tagsResponse.data,
      });
    } catch (e) {
      this.matchError.logError(e);
    }
  }

  public deleteFromData(id: number): void {
    this.emailsPageStateService.filterDataTable([id]);
  }

  public changeDraftData(data: any): void {
    if (
      this.emailsPageStateService.infoOpenedMailAccount.type ===
      this.mailFolderType.Draft
    ) {
      const newData: Partial<TransformEmailListItemContract> = {
        to: data.updateRequest.to,
        subject: data.updateRequest.subject,
        hasAttachments: !!data.updateRequest.attachmentFiles.length,
      };

      data.isNotesChanged && (newData.notesCount = 1);
      this.emailsPageStateService.changeObjectDataTable(
        [data.emailId],
        newData
      );
    }
  }

  public async closeEmailModalForm(data?: {
    emailsId: number[];
    tags: string[];
  }): Promise<any> {
    const isDeleted =
      this.openedEmailInfo?.deleted ===
      this.emailsPageStateService.openedEmail?.deleted;
    const isSpam =
      this.openedEmailInfo?.isSpam ===
      this.emailsPageStateService.openedEmail?.isSpam;
    const isSeen = this.openedEmailInfo?.seen;

    if (isDeleted && isSpam) {
      data && (await this.emailTagClient.saveTags(data));
      this.emailsPageStateService.openedEmail?.emailId &&
        this.getTags(isSeen, this.emailsPageStateService.openedEmail.emailId);
    } else {
      this.emailsPageStateService.infoOpenedMailAccount.acc &&
        this.userWorkspaceService.refreshUnread({
          mailAccountId: this.emailsPageStateService.infoOpenedMailAccount.acc,
        });
      if (this.openedEmailInfo) {
        this.emailsPageStateService.openedEmail?.emailId &&
          this.emailsPageStateService.filterDataTable([
            this.emailsPageStateService.openedEmail.emailId,
          ]);
        // this.emailsService.reloadDataOnDemand();
      }
    }
    this.emailsPageStateService.openedEmail = null;
    this.openedEmailInfo = null;
  }

  public loadingImportEML($event: boolean): void {
    this.isLoadingEML = $event;
  }

  async sortData(sort: Sort): Promise<void> {
    this.emailsPageStateService.searchOrder = sort?.direction
      ? (this.emailsPageStateService.searchOrders.find(
          el => el.name === sort.active
        )?.id ?? EmailSearchOrder.OrderBySentDateTime)
      : EmailSearchOrder.OrderBySentDateTime;
    this.emailsPageStateService.orderDirection =
      sort?.direction === 'desc'
        ? OrderDirection.Descending
        : OrderDirection.Ascending;

    await this.changePage(!!sort?.direction);
  }

  async showOnlyUnseen(value: boolean): Promise<void> {
    this.emailsPageStateService.onlyUnseen = value ? null : false;
    await this.changePage(!!this.emailsDesktopComponent?.sort?.direction);
  }

  ngOnDestroy(): void {
    this.routeSubscribe$?.unsubscribe();
    this.useMobileViewSubscription?.unsubscribe();
    this.setInterval && clearInterval(this.setInterval);
    this.setIntervalTag && clearInterval(this.setIntervalTag);
    this.setIntervalGroup && clearInterval(this.setIntervalGroup);
  }

  ngAfterViewInit(): void {
    this.actionsModals = this.buttonActionsService.actionsModals;
    this.actions = this.buttonActionsService.actions;
    this.actionsModals.createOrUpdateDrawer = this.createOrUpdateDrawer;
    this.actionsModals.auditDrawer = this.auditDrawer;
    this.actionsModals.metaDrawer = this.metaDrawer;
    this.actionsModals.addGroupDrawer = this.addGroupDrawer;
    this.actionsModals.addTagDrawer = this.addTagDrawer;
    this.actionsModals.emailDrawer = this.emailDrawer;
    this.actionsModals.notificationDrawer = this.notificationDrawer;
    this.actionsModals.notesDrawer = this.notesDrawer;
  }
}
